import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './CollaboratorItem.sass'
import Avatar, { Badge } from 'components/Avatar'
import { CollaboratorViewModel } from 'jobs/views/JobRoot/Job/CollaboratorsCard/CollaboratorViewModel'
import JobViewModel from 'jobs/views/JobRoot/Job/JobViewModel'
import { truncate } from 'lodash'
import Tooltip from 'components/Tooltip'
import { Id } from 'types/definitions'
import Link from 'components/Link'
import link from 'infra/link'

interface Props {
  vm: JobViewModel
  collaborator: CollaboratorViewModel
}

export default observer(function CollaboratorItem({ collaborator }: Props) {
  return (
    <div
      className={cx(
        styles.root,
        collaborator.team && styles.withTeam,
        collaborator.member.email && styles.withEmail,
        collaborator.member.phone && styles.withPhone,
      )}
      data-test={`collaborator-item-${collaborator.member.displayName}`}
    >
      <div className={styles.info}>
        <LinkToUserDetails
          canViewUserDetails={collaborator.canViewUserDetails}
          collaboratorId={collaborator.member.user.publicId}
        >
          <p
            data-test={`collaborator-item-name-${collaborator.member.displayName}`}
            className={styles.title}
          >
            {collaborator.member.displayName}
          </p>
        </LinkToUserDetails>

        <div className={styles.subtitle}>
          {collaborator.canViewCollaboratorInfo && (
            <div className={styles.subtitleInfo}>
              {collaborator.team && (
                <p className={styles.team}>{collaborator.team.name}</p>
              )}
              {collaborator.canViewCollaboratorEmail && (
                <>
                  {collaborator.member.email.length >= 38 ? (
                    <Tooltip content={collaborator.member.email}>
                      <p className={styles.email}>
                        {truncate(collaborator.member.email, { length: 38 })}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className={styles.email}>{collaborator.member.email}</p>
                  )}
                </>
              )}
              {collaborator.canViewCollaboratorPhoneNumber && (
                <p className={styles.phone}>{collaborator.member.phone}</p>
              )}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <p>
            <span
              className={cx(
                collaborator.type === 'CLIENT' && styles.client,
                collaborator.type === 'PROVIDER' && styles.pro,
              )}
            >
              {collaborator.formattedType}
            </span>
            <span className={styles.separator}>|</span>
            <span
              data-test={`collaborator-item-role-${collaborator.member.displayName}`}
            >
              {collaborator.formattedRole}
            </span>
          </p>

          <LinkToUserDetails
            canViewUserDetails={collaborator.canViewUserDetails}
            collaboratorId={collaborator.member.user.publicId}
          >
            <div className={styles.avatarContainer}>
              <Avatar
                badge={
                  collaborator.member.user.online ? Badge.Online : Badge.Offline
                }
                size={32}
                subject={collaborator.member}
              />
            </div>
          </LinkToUserDetails>
        </div>
      </div>
    </div>
  )
})

interface LinkToUserDetails {
  children: React.ReactNode
  canViewUserDetails: boolean
  collaboratorId: Id
}

/**
 * Internal component for linking to user details.
 */
function LinkToUserDetails({
  children,
  canViewUserDetails,
  collaboratorId,
}: LinkToUserDetails) {
  return canViewUserDetails ? (
    <Link
      className={styles.hqUserDetailsLink}
      to={link.userDetails(Id.assert(collaboratorId))}
    >
      {children}
    </Link>
  ) : (
    children
  )
}
